import { readFile } from 'node:fs/promises';
import { existsSync } from 'node:fs';
import { Container, ProcessTerminal, Spacer, Text, TUI } from '@mariozechner/pi-tui';
import type {
  ApprovalDecision,
  ToolEndEvent,
  ToolErrorEvent,
  ToolStartEvent,
} from './agent/index.js';
import {
  getApiKeyNameForProvider,
  getApiKeyNameForSearchProvider,
  getProviderDisplayName,
  getSearchProviderDisplayName,
} from './utils/env.js';
import { maxPath } from './utils/paths.js';
import { defaultQueue } from './utils/message-queue.js';
import { logger } from './utils/logger.js';
import {
  AgentRunnerController,
  InputHistoryController,
  KeySelectionController,
  ModelSelectionController,
  OnboardingController,
  SearchSelectionController,
} from './controllers/index.js';
import {
  ApiKeyInputComponent,
  ApprovalPromptComponent,
  QuestionPromptComponent,
  ChatLogComponent,
  CustomEditor,
  DebugPanelComponent,
  HintBarComponent,
  IntroComponent,
  WorkingIndicatorComponent,
  createApiKeyConfirmSelector,
  createDrishtiApiKeyActionSelector,
  createFactoryResetScopeSelector,
  createKeyActionSelector,
  createKeyServiceSelector,
  createModelSelector,
  createProviderSelector,
  createSearchProviderSelector,
} from './components/index.js';
import { checkApiKeyExists } from './utils/env.js';
import { editorTheme, theme } from './theme.js';
import { matchCommands, type SlashCommand } from './commands/index.js';
import { buildGuideSections, CHANNEL_GUIDE } from './commands/guide.js';
import { loadGatewayConfig, getGatewayConfigPath } from './gateway/config.js';
import { initSpinner } from './utils/spinner.js';
import {
  FACTORY_RESET_SCOPES,
  performFactoryReset,
  type FactoryResetScope,
} from './utils/factory-reset.js';
import { checkForUpdate, UPDATE_CHECK_PACKAGE_NAME } from './utils/update-check.js';

function truncateForHistory(text: string): string {
  const lines = text.split('\n');
  if (lines.length <= 3) return text;
  const firstLine = lines[0].trim() || lines[1]?.trim() || 'pasted content';
  const preview = firstLine.length > 60 ? firstLine.slice(0, 60) + '...' : firstLine;
  return `${preview} [+${lines.length - 1} lines]`;
}

function truncateAtWord(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }
  const lastSpace = str.lastIndexOf(' ', maxLength);
  if (lastSpace > maxLength * 0.5) {
    return `${str.slice(0, lastSpace)}...`;
  }
  return `${str.slice(0, maxLength)}...`;
}

function summarizeToolResult(tool: string, args: Record<string, unknown>, result: string): string {
  if (tool === 'skill') {
    const skillName = args.skill as string;
    return `Loaded ${skillName} skill`;
  }
  try {
    const parsed = JSON.parse(result);
    if (parsed.data) {
      if (Array.isArray(parsed.data)) {
        return `Received ${parsed.data.length} items`;
      }
      if (typeof parsed.data === 'object') {
        const keys = Object.keys(parsed.data).filter((key) => !key.startsWith('_'));
        if (
          tool === 'company_research' ||
          tool === 'earnings_analysis' ||
          tool === 'earnings_metric_comparison' ||
          tool === 'market_pulse' ||
          tool === 'portfolio_brief' ||
          tool === 'peer_group_research' ||
          tool === 'price_volume_signal_explainer' ||
          tool === 'result_season_tracker' ||
          tool === 'concall_source_pack' ||
          tool === 'filing_source_pack' ||
          tool === 'orders_capex_tracker' ||
          tool === 'corporate_action_monitor' ||
          tool === 'governance_monitor' ||
          tool === 'risk_monitor' ||
          tool === 'event_calendar' ||
          tool === 'get_financials' ||
          tool === 'get_market_data'
        ) {
          if (keys.length === 0) return 'Done';
          return keys.length === 1 ? 'Called 1 data source' : `Called ${keys.length} data sources`;
        }
        if (tool === 'web_search') {
          return 'Did 1 search';
        }
        return `Received ${keys.length} fields`;
      }
    }
  } catch {
    return truncateAtWord(result, 50);
  }
  return 'Received data';
}

function createScreen(
  title: string,
  description: string,
  body: any,
  footer?: string,
): Container {
  const container = new Container();
  if (title) {
    container.addChild(new Text(theme.bold(theme.primary(title)), 0, 0));
  }
  if (description) {
    container.addChild(new Text(theme.muted(description), 0, 0));
  }
  container.addChild(new Spacer(1));
  container.addChild(body);
  if (footer) {
    container.addChild(new Spacer(1));
    container.addChild(new Text(theme.muted(footer), 0, 0));
  }
  return container;
}

/**
 * Render a single display event into the chat log (used by incremental history).
 */
function renderEvent(
  chatLog: ChatLogComponent,
  display: { event: any; id: string; completed?: boolean; endEvent?: any; progressMessage?: string },
  itemStatus: string,
) {
  const event = display.event;

  if (event.type === 'thinking') {
    const message = event.message.trim();
    if (message) {
      chatLog.addChild(
        new Text(message.length > 200 ? `${message.slice(0, 200)}...` : message, 0, 0),
      );
    }
    return;
  }

  if (event.type === 'tool_start') {
    const toolStart = event as ToolStartEvent;
    // ask_user_question has no tool row: the inline widget is its UI, and an
    // answered block is appended on submit. (matches the no-tool-message design)
    if (toolStart.tool === 'ask_user_question') {
      return;
    }
    const component = chatLog.startTool(display.id, toolStart.tool, toolStart.args);
    if (display.completed && display.endEvent?.type === 'tool_end') {
      const done = display.endEvent as ToolEndEvent;
      component.setComplete(
        summarizeToolResult(done.tool, toolStart.args, done.result),
        done.duration,
      );
    } else if (display.completed && display.endEvent?.type === 'tool_error') {
      const toolError = display.endEvent as ToolErrorEvent;
      component.setError(toolError.error);
    } else if (itemStatus === 'interrupted') {
      // Don't start spinner for tools in interrupted items
    } else if (display.progressMessage) {
      component.setActive(display.progressMessage);
    }
    return;
  }

  if (event.type === 'tool_approval') {
    chatLog.startTool(display.id, event.tool, event.args).setApproval(event.approved);
    return;
  }

  if (event.type === 'tool_denied') {
    const path = (event.args.path as string) ?? '';
    chatLog.startTool(display.id, event.tool, event.args).setDenied(path, event.tool);
    return;
  }

  if (event.type === 'tool_limit') return;

  if (event.type === 'context_cleared') {
    chatLog.addContextCleared(event.clearedCount, event.keptCount);
  }
  if (event.type === 'microcompact') {
    chatLog.addMicrocompact(event.cleared, event.tokensSaved);
  }
  if (event.type === 'queue_drain') {
    chatLog.addQueueDrain(event.messageCount);
  }
  if (event.type === 'compaction' && event.phase === 'end') {
    chatLog.addCompaction(event.success ?? false, event.preCompactTokens, event.postCompactTokens);
  }
}

export async function runCli() {
  const tui = new TUI(new ProcessTerminal());
  const root = new Container();
  const chatLog = new ChatLogComponent(tui);
  const inputHistory = new InputHistoryController(() => tui.requestRender());
  let lastError: string | null = null;
  let transientWarning: string | null = null;
  let ctrlCExitPending = false;
  let ctrlCTimeout: ReturnType<typeof setTimeout> | null = null;

  const onError = (message: string) => {
    lastError = message;
    logger.error(message);
    tui.requestRender();
  };

  let agentRunner: AgentRunnerController;
  const modelSelection = new ModelSelectionController(onError, () => {
    intro.setModel(modelSelection.model);
    agentRunner?.updateAgentConfig({
      model: modelSelection.model ?? undefined,
      modelProvider: modelSelection.provider,
    });
    renderSelectionOverlay();
    tui.requestRender();
  });
  const searchSelection = new SearchSelectionController(onError, () => {
    renderSelectionOverlay();
    tui.requestRender();
  });
  const keySelection = new KeySelectionController(
    onError,
    (message: string) => {
      lastError = null;
      refreshError();
      chatLog.addChild(new Spacer(1));
      chatLog.addChild(new Text(theme.muted(message), 0, 0));
      tui.requestRender();
    },
    () => {
      renderSelectionOverlay();
      tui.requestRender();
    },
  );
  const addInfoMessage = (message: string) => {
    lastError = null;
    refreshError();
    chatLog.addChild(new Spacer(1));
    chatLog.addChild(new Text(theme.muted(message), 0, 0));
    tui.requestRender();
  };
  const onboarding = new OnboardingController(
    onError,
    (provider, model) => {
      modelSelection.setModelSelection(provider, model);
      addInfoMessage('Setup complete. Max is ready for financial research.');
    },
    () => {
      renderSelectionOverlay();
      tui.requestRender();
    },
  );
  type FactoryResetScreen = 'idle' | 'scope_select' | 'confirm';
  let factoryResetScreen: FactoryResetScreen = 'idle';
  let pendingFactoryResetScope: FactoryResetScope | null = null;

  // Incremental history tracking
  let lastRenderedEventCount = 0;
  let lastRenderedStatus = '';
  let lastRenderedAnswer = false;
  let lastRenderedQueryId: string | null = null;
  const finalizedToolIds = new Set<string>();
  const appliedToolProgress = new Map<string, string>();
  let lastPendingApproval: { tool: string; args: Record<string, unknown> } | null = null;
  let lastPendingQuestion: { questions: unknown[] } | null = null;
  // Cached so the stateful question overlay survives onChange-driven re-renders
  // (partial selections, active tab, in-progress text are kept across renders).
  let activeQuestionPrompt: QuestionPromptComponent | null = null;

  agentRunner = new AgentRunnerController(
    { model: modelSelection.model ?? undefined, modelProvider: modelSelection.provider, maxIterations: 10 },
    modelSelection.inMemoryChatHistory,
    () => {
      // Incremental history update — only render new events
      const history = agentRunner.history;
      const lastItem = history[history.length - 1];
      if (lastItem) {
        // New query started — keyed by id so onChange storms don't re-render the header
        if (lastItem.id !== lastRenderedQueryId) {
          chatLog.addQuery(lastItem.query);
          chatLog.resetToolGrouping();
          lastRenderedQueryId = lastItem.id;
        }

        // Render new events only
        for (let i = lastRenderedEventCount; i < lastItem.events.length; i++) {
          renderEvent(chatLog, lastItem.events[i], lastItem.status);
        }
        lastRenderedEventCount = lastItem.events.length;

        // Update already-rendered tool events that may have completed
        for (const display of lastItem.events) {
          if (display.event.type === 'tool_start' && display.completed && display.endEvent && !finalizedToolIds.has(display.id)) {
            const component = chatLog.getToolById(display.id);
            if (component) {
              finalizedToolIds.add(display.id);
              if (display.endEvent.type === 'tool_end') {
                component.setComplete(
                  summarizeToolResult(display.endEvent.tool, display.event.args, display.endEvent.result),
                  display.endEvent.duration,
                );
              } else if (display.endEvent.type === 'tool_error') {
                component.setError(display.endEvent.error);
              }
            }
          }
        }

        // Apply live progress to already-rendered, still-running tools. Guarded
        // by change-detection so each unique message triggers exactly one update.
        for (const display of lastItem.events) {
          if (
            display.event.type === 'tool_start' &&
            !display.completed &&
            display.progressMessage &&
            appliedToolProgress.get(display.id) !== display.progressMessage
          ) {
            appliedToolProgress.set(display.id, display.progressMessage);
            chatLog.getToolById(display.id)?.setActive(display.progressMessage);
          }
        }

        // Handle completion
        if (lastItem.answer && !lastRenderedAnswer) {
          chatLog.finalizeAnswer(lastItem.answer);
          lastRenderedAnswer = true;
        }
        if (lastItem.status === 'complete' && lastRenderedStatus !== 'complete') {
          chatLog.addPerformanceStats(lastItem.duration ?? 0, lastItem.tokenUsage, lastItem.tokensPerSecond);
        }
        if (lastItem.status === 'interrupted' && lastRenderedStatus !== 'interrupted') {
          // Stop all active tool spinners on interrupt
          for (const display of lastItem.events) {
            if (display.event.type === 'tool_start' && !finalizedToolIds.has(display.id)) {
              const component = chatLog.getToolById(display.id);
              component?.dispose?.();
              finalizedToolIds.add(display.id);
            }
          }
          chatLog.addInterrupted();
        }
        lastRenderedStatus = lastItem.status;
      }

      workingIndicator.setState(agentRunner.workingState);
      updateView();
      if (agentRunner.pendingApproval !== lastPendingApproval) {
        lastPendingApproval = agentRunner.pendingApproval;
        renderSelectionOverlay();
      }
      if (agentRunner.pendingQuestion !== lastPendingQuestion) {
        lastPendingQuestion = agentRunner.pendingQuestion;
        renderSelectionOverlay();
      }
      throttledRender();
    },
  );

  const intro = new IntroComponent(modelSelection.model);
  const errorText = new Text('', 0, 0);
  const workingIndicator = new WorkingIndicatorComponent(tui);
  workingIndicator.setTurnStatsProvider(() => agentRunner.turnStats);
  const editor = new CustomEditor(tui, editorTheme);
  const hintBar = new HintBarComponent();
  const debugPanel = new DebugPanelComponent(8, true);
  const spacer = new Spacer(1);

  // Build the component tree ONCE — stable structure, no root.clear()
  root.addChild(intro);
  root.addChild(chatLog);
  root.addChild(errorText);
  root.addChild(workingIndicator);
  root.addChild(spacer);
  root.addChild(editor);
  root.addChild(hintBar);
  root.addChild(debugPanel);
  tui.addChild(root);
  initSpinner(tui);

  // Render throttle for agent events (~30fps max)
  let renderPending = false;
  const RENDER_THROTTLE_MS = 32;
  function throttledRender(): void {
    if (renderPending) return;
    renderPending = true;
    setTimeout(() => {
      renderPending = false;
      tui.requestRender();
    }, RENDER_THROTTLE_MS);
  }

  const refreshError = () => {
    if (transientWarning) {
      errorText.setText(theme.warning(transientWarning));
      return;
    }
    const message = lastError ?? agentRunner.error;
    errorText.setText(message ? theme.error(`Error: ${message}`) : '');
  };

  const requestCtrlCExit = () => {
    if (ctrlCExitPending) {
      if (ctrlCTimeout) {
        clearTimeout(ctrlCTimeout);
        ctrlCTimeout = null;
      }
      tui.stop();
      process.exit(130);
    }

    ctrlCExitPending = true;
    transientWarning = 'Press Ctrl+C again to exit.';
    refreshError();
    tui.requestRender();
    ctrlCTimeout = setTimeout(() => {
      ctrlCExitPending = false;
      transientWarning = null;
      ctrlCTimeout = null;
      refreshError();
      tui.requestRender();
    }, 2000);
  };

  const bindCtrlCExit = <T extends { onCtrlC?: () => void }>(component: T): T => {
    component.onCtrlC = requestCtrlCExit;
    return component;
  };

  // Slash command autocomplete state
  let slashSuggestions: SlashCommand[] = [];
  let slashSelectedIndex = 0;
  let slashActive = false;

  const HELP_TEXT = `Keyboard Shortcuts
  esc          Interrupt query / clear input
  ctrl+c x2    Exit Max
  /guide       Show everything Max can do
  /model       Switch LLM provider and model
  /search      Choose preferred web search provider
  /keys        Manage stored API keys
  /factory-reset Reset settings, credentials, or all Max data
  /gateway     Connect Max to WhatsApp
  /rules       Show research rules
  /clear       Clear conversation
  ↑ / ↓        Navigate input history`;

  const requireModelSelected = (): boolean => {
    if (modelSelection.hasModelSelected) {
      return true;
    }
    onError('No model selected. Run /model to choose one.');
    return false;
  };

  const handleSlashCommand = async (command: string) => {
    switch (command) {
      case 'guide': {
        chatLog.addChild(new Spacer(1));
        for (const section of buildGuideSections(modelSelection.model)) {
          chatLog.addChild(new Text(theme.bold(theme.primary(section.title)), 0, 0));
          for (const line of section.lines) {
            chatLog.addChild(new Text(theme.muted(line), 0, 0));
          }
          chatLog.addChild(new Spacer(1));
        }
        tui.requestRender();
        break;
      }
      case 'model':
        modelSelection.startSelection();
        break;
      case 'search':
        searchSelection.startSelection();
        break;
      case 'keys':
        keySelection.startSelection();
        break;
      case 'factory-reset':
        factoryResetScreen = 'scope_select';
        pendingFactoryResetScope = null;
        renderSelectionOverlay();
        break;
      case 'gateway': {
        chatLog.addChild(new Spacer(1));
        chatLog.addChild(new Text(theme.bold(theme.primary('WhatsApp Gateway')), 0, 0));
        try {
          const cfg = loadGatewayConfig();
          const linked = cfg.channels.whatsapp.allowFrom;
          if (existsSync(getGatewayConfigPath()) && linked.length > 0) {
            chatLog.addChild(new Text(theme.success(`Status: linked (${linked.join(', ')})`), 0, 0));
          } else {
            chatLog.addChild(new Text(theme.muted('Status: not linked yet'), 0, 0));
          }
        } catch {
          chatLog.addChild(new Text(theme.muted('Status: not configured'), 0, 0));
        }
        const steps = [
          'Chat with Max from your phone:',
          '  1. Link your phone:    max gateway login   (scan the QR code)',
          '  2. Start the gateway:  max gateway run',
          '  3. Message yourself on WhatsApp — Max replies in that chat.',
          'The gateway is a separate long-running process — run it in another terminal.',
        ];
        for (const line of steps) {
          chatLog.addChild(new Text(theme.muted(line), 0, 0));
        }
        tui.requestRender();
        break;
      }
      case 'rules': {
        try {
          const rulesContent = await readFile(maxPath('RULES.md'), 'utf-8');
          chatLog.addChild(new Spacer(1));
          chatLog.addChild(new Text(theme.muted('Research Rules:'), 0, 0));
          chatLog.addChild(new Text(rulesContent, 0, 0));
        } catch {
          chatLog.addChild(new Spacer(1));
          chatLog.addChild(new Text(theme.muted('No research rules set. Use "add a rule <text>" to create one.'), 0, 0));
        }
        tui.requestRender();
        break;
      }
      case 'clear':
        chatLog.clearAll();
        tui.requestRender();
        break;
      case 'memory':
        if (!requireModelSelected()) break;
        await agentRunner.runQuery('Show me what you know about me from memory. Use memory_search and memory_get.');
        break;
      case 'heartbeat':
        if (!requireModelSelected()) break;
        await agentRunner.runQuery('Show me my current heartbeat checklist from .max/HEARTBEAT.md');
        break;
      case 'history': {
        const messages = modelSelection.inMemoryChatHistory.getMessages();
        chatLog.addChild(new Spacer(1));
        if (messages.length === 0) {
          chatLog.addChild(new Text(theme.muted('No conversation history yet.'), 0, 0));
        } else {
          chatLog.addChild(new Text(theme.muted('Recent conversations:'), 0, 0));
          for (const msg of messages) {
            const summary = msg.summary ?? msg.answer?.slice(0, 100) ?? '(pending)';
            chatLog.addChild(new Text(theme.muted(`  ${msg.id + 1}. ${msg.query}`), 0, 0));
            chatLog.addChild(new Text(theme.muted(`     ${summary}`), 0, 0));
          }
        }
        tui.requestRender();
        break;
      }
      case 'help':
        chatLog.addChild(new Spacer(1));
        chatLog.addChild(new Text(theme.muted(HELP_TEXT), 0, 0));
        tui.requestRender();
        break;
    }
  };

  // Slash callbacks are wired after renderSelectionOverlay is defined (below)

  const handleSubmit = async (query: string) => {
    if (query.toLowerCase() === 'exit' || query.toLowerCase() === 'quit') {
      tui.stop();
      process.exit(0);
      return;
    }

    // Handle all slash commands
    if (query.startsWith('/')) {
      const command = query.slice(1).trim().toLowerCase();
      slashActive = false;
      slashSuggestions = [];
      await handleSlashCommand(command);
      return;
    }

    if (
      modelSelection.isInSelectionFlow() ||
      searchSelection.isInSelectionFlow() ||
      keySelection.isInSelectionFlow() ||
      onboarding.isInSelectionFlow() ||
      factoryResetScreen !== 'idle' ||
      agentRunner.pendingApproval
    ) {
      return;
    }

    if (!requireModelSelected()) {
      return;
    }

    // If agent is busy, enqueue the message for mid-run injection
    if (agentRunner.isProcessing) {
      defaultQueue.enqueue({
        text: query,
        priority: 'next',
        enqueuedAt: Date.now(),
        source: 'cli',
      });
      await inputHistory.saveMessage(query);
      chatLog.addQueuedMessage(query);
      tui.requestRender();
      return;
    }

    await inputHistory.saveMessage(query);
    inputHistory.resetNavigation();
    lastRenderedEventCount = 0;
    lastRenderedStatus = '';
    lastRenderedAnswer = false;
    finalizedToolIds.clear();
    const result = await agentRunner.runQuery(query);
    if (result?.answer) {
      await inputHistory.updateAgentResponse(result.answer);
    }
    refreshError();
    tui.requestRender();
  };

  editor.onSubmit = (text) => {
    const displayValue = text.trim();
    if (!displayValue) return;
    const fullValue = editor.getFullText(displayValue);
    editor.setText('');
    editor.addToHistoryWithTruncation(fullValue);
    void handleSubmit(fullValue);
  };

  let escPendingClear = false;
  let escPendingExit = false;
  let escTimeout: ReturnType<typeof setTimeout> | null = null;

  // onEscape is wired after renderSelectionOverlay is defined (below)

  editor.onCtrlC = () => {
    requestCtrlCExit();
  };

  /**
   * Update component state without rebuilding the tree.
   * The root is built once at init — this only changes text/hints/visibility.
   */
  const updateView = () => {
    refreshError();
    if (slashActive && slashSuggestions.length > 0) {
      hintBar.setSuggestions(slashSuggestions, slashSelectedIndex);
    } else {
      hintBar.clearSuggestions();
      hintBar.update({
        isProcessing: agentRunner.isProcessing,
        hasPendingApproval: !!agentRunner.pendingApproval,
        hasInput: editor.getText().trim().length > 0,
        escPendingClear,
        escPendingExit,
        queueLength: defaultQueue.length(),
      });
    }
    if (
      !modelSelection.isInSelectionFlow() &&
      !searchSelection.isInSelectionFlow() &&
      !keySelection.isInSelectionFlow() &&
      !onboarding.isInSelectionFlow() &&
      factoryResetScreen === 'idle' &&
      !agentRunner.pendingApproval &&
      !agentRunner.pendingQuestion
    ) {
      tui.setFocus(editor);
    }
  };

  /**
   * Show a full-screen selection view by replacing the root content.
   * Used for infrequent user-initiated overlays (model selection, approval).
   */
  const showScreenView = (
    title: string,
    description: string,
    body: any,
    footer?: string,
    focusTarget?: any,
  ) => {
    root.clear();
    refreshError();
    root.addChild(createScreen(title, description, body, footer));
    root.addChild(errorText);
    if (focusTarget) {
      tui.setFocus(focusTarget);
    }
  };

  /**
   * Restore the main view after an overlay screen closes.
   */
  const restoreMainView = () => {
    root.clear();
    root.addChild(intro);
    root.addChild(chatLog);
    root.addChild(errorText);
    root.addChild(workingIndicator);
    root.addChild(spacer);
    root.addChild(editor);
    root.addChild(hintBar);
    root.addChild(debugPanel);
    updateView();
  };

  // Render the question widget INLINE: keep the conversation (intro + chatLog)
  // visible and slot the widget where the input normally sits, instead of
  // replacing the whole screen via showScreenView.
  const showQuestionInline = () => {
    if (!activeQuestionPrompt) return;
    root.clear();
    root.addChild(intro);
    root.addChild(chatLog);
    root.addChild(errorText);
    root.addChild(spacer);
    root.addChild(activeQuestionPrompt);
    root.addChild(debugPanel);
    tui.setFocus(activeQuestionPrompt);
  };

  const renderSelectionOverlay = () => {
    const state = modelSelection.state;
    const searchState = searchSelection.state;
    const keyState = keySelection.state;
    const onboardingState = onboarding.state;
    if (
      state.appState === 'idle' &&
      searchState.appState === 'idle' &&
      keyState.appState === 'idle' &&
      !onboarding.isInSelectionFlow() &&
      factoryResetScreen === 'idle' &&
      !agentRunner.pendingApproval &&
      !agentRunner.pendingQuestion
    ) {
      activeQuestionPrompt = null;
      restoreMainView();
      tui.requestRender();
      return;
    }

    if (agentRunner.pendingApproval) {
      const prompt = new ApprovalPromptComponent(
        agentRunner.pendingApproval.tool,
        agentRunner.pendingApproval.args,
      );
      bindCtrlCExit(prompt.selector);
      prompt.onSelect = (decision: ApprovalDecision) => {
        agentRunner.respondToApproval(decision);
      };
      showScreenView('', '', prompt, undefined, prompt.selector);
      return;
    }

    if (agentRunner.pendingQuestion) {
      if (!activeQuestionPrompt) {
        activeQuestionPrompt = new QuestionPromptComponent(
          agentRunner.pendingQuestion.questions,
          tui,
        );
        activeQuestionPrompt.onSubmit = (answers) => {
          chatLog.addAnsweredQuestions(answers.answers);
          agentRunner.respondToQuestion(answers);
        };
        activeQuestionPrompt.onCancel = () => {
          agentRunner.respondToQuestion({ answers: [], declined: true });
        };
        activeQuestionPrompt.onAbort = () => {
          requestCtrlCExit();
        };
      }
      showQuestionInline();
      return;
    }

    if (onboardingState.appState === 'provider_select') {
      const selector = bindCtrlCExit(createProviderSelector(modelSelection.provider, (providerId) => {
        void onboarding.handleProviderSelect(providerId);
      }));
      showScreenView(
        'Set up Max',
        'Max needs a model key and a Drishti key to run financial research.',
        selector,
        'Choose a model provider · esc to exit setup',
        selector,
      );
      return;
    }

    if (onboardingState.appState === 'model_select' && onboardingState.pendingProvider) {
      const selector = bindCtrlCExit(createModelSelector(
        onboardingState.pendingModels,
        undefined,
        (modelId) => onboarding.handleModelSelect(modelId),
        onboardingState.pendingProvider,
      ));
      showScreenView(
        `Choose model for ${getProviderDisplayName(onboardingState.pendingProvider)}`,
        'This will be saved for future Max sessions.',
        selector,
        'Enter to confirm · esc to go back',
        selector,
      );
      return;
    }

    if (onboardingState.appState === 'model_input' && onboardingState.pendingProvider) {
      const input = bindCtrlCExit(new ApiKeyInputComponent());
      input.onSubmit = (value) => onboarding.handleModelInputSubmit(value);
      input.onCancel = () => onboarding.handleModelInputSubmit(null);
      showScreenView(
        `Enter model name for ${getProviderDisplayName(onboardingState.pendingProvider)}`,
        'Type or paste the model name from openrouter.ai/models',
        input,
        'Examples: anthropic/claude-3.5-sonnet, openai/gpt-4-turbo, meta-llama/llama-3-70b\nEnter to confirm · esc to go back',
        input,
      );
      return;
    }

    if (onboardingState.appState === 'llm_key_input' && onboardingState.pendingProvider) {
      const input = bindCtrlCExit(new ApiKeyInputComponent(true));
      input.onSubmit = (apiKey) => onboarding.handleLlmKeySubmit(apiKey);
      input.onCancel = () => onboarding.handleLlmKeyCancel();
      const apiKeyName = getApiKeyNameForProvider(onboardingState.pendingProvider) ?? '';
      showScreenView(
        `Enter ${getProviderDisplayName(onboardingState.pendingProvider)} API Key`,
        apiKeyName ? `(${apiKeyName})` : '',
        input,
        'Enter to confirm · Esc to stay here',
        input,
      );
      return;
    }

    if (onboardingState.appState === 'drishti_confirm') {
      const selector = bindCtrlCExit(createDrishtiApiKeyActionSelector((action) =>
        onboarding.handleDrishtiAction(action),
      ));
      showScreenView(
        'Step 2 of 2 - Connect Drishti',
        'Max uses Drishti to research Indian stocks.\nUse the arrow keys and press Enter.',
        selector,
        '↑/↓ Navigate • Enter Select',
        selector,
      );
      return;
    }

    if (onboardingState.appState === 'drishti_key_input') {
      const input = bindCtrlCExit(new ApiKeyInputComponent(true));
      input.onSubmit = (apiKey) => void onboarding.handleDrishtiKeySubmit(apiKey);
      input.onCancel = () => onboarding.handleDrishtiKeyCancel();
      const description = onboardingState.openedPortal
        ? `Opened the developer portal.\nCreate your API key, then return here.\n${onboardingState.portalUrl}`
        : 'Paste your Drishti API key.';
      showScreenView(
        'Paste your API key',
        description,
        input,
        'Enter to save · Esc to stay here',
        input,
      );
      return;
    }

    if (factoryResetScreen === 'scope_select') {
      const selector = bindCtrlCExit(createFactoryResetScopeSelector((scope) => {
        if (!scope) {
          factoryResetScreen = 'idle';
          pendingFactoryResetScope = null;
          renderSelectionOverlay();
          return;
        }
        pendingFactoryResetScope = scope;
        factoryResetScreen = 'confirm';
        renderSelectionOverlay();
      }));
      showScreenView(
        'Factory reset',
        'Choose what Max should delete.',
        selector,
        'Enter to choose · esc to cancel',
        selector,
      );
      return;
    }

    if (factoryResetScreen === 'confirm' && pendingFactoryResetScope) {
      const input = bindCtrlCExit(new ApiKeyInputComponent());
      const scope = FACTORY_RESET_SCOPES[pendingFactoryResetScope];
      input.onSubmit = (value) => {
        if (value !== 'RESET') {
          onError('Factory reset cancelled. Type RESET exactly to confirm.');
          factoryResetScreen = 'idle';
          pendingFactoryResetScope = null;
          renderSelectionOverlay();
          return;
        }
        try {
          const result = performFactoryReset(pendingFactoryResetScope!);
          addInfoMessage(
            `Factory reset complete (${scope.title}). Deleted ${result.deleted.length} item${result.deleted.length === 1 ? '' : 's'}. Max will exit now.`,
          );
          factoryResetScreen = 'idle';
          pendingFactoryResetScope = null;
          setTimeout(() => {
            tui.stop();
            process.exit(0);
          }, 50);
          return;
        } catch (error) {
          onError(error instanceof Error ? error.message : String(error));
        }
        factoryResetScreen = 'idle';
        pendingFactoryResetScope = null;
        renderSelectionOverlay();
      };
      input.onCancel = () => {
        factoryResetScreen = 'idle';
        pendingFactoryResetScope = null;
        renderSelectionOverlay();
      };
      showScreenView(
        `Confirm ${scope.title}`,
        `Warning: this will delete ${scope.description}.\nType RESET to confirm.`,
        input,
        'Enter to confirm · Esc to cancel',
        input,
      );
      return;
    }

    if (state.appState === 'provider_select') {
      const selector = bindCtrlCExit(createProviderSelector(modelSelection.provider, (providerId) => {
        void modelSelection.handleProviderSelect(providerId);
      }));
      showScreenView(
        'Select provider',
        'Switch between LLM providers. Applies to this session and future sessions.',
        selector,
        'Enter to confirm · esc to exit',
        selector,
      );
      return;
    }

    if (state.appState === 'model_select' && state.pendingProvider) {
      const selector = bindCtrlCExit(createModelSelector(
        state.pendingModels,
        modelSelection.provider === state.pendingProvider ? modelSelection.model ?? undefined : undefined,
        (modelId) => modelSelection.handleModelSelect(modelId),
        state.pendingProvider,
      ));
      showScreenView(
        `Select model for ${getProviderDisplayName(state.pendingProvider)}`,
        '',
        selector,
        'Enter to confirm · esc to go back',
        selector,
      );
      return;
    }

    if (state.appState === 'model_input' && state.pendingProvider) {
      const input = bindCtrlCExit(new ApiKeyInputComponent());
      input.onSubmit = (value) => modelSelection.handleModelInputSubmit(value);
      input.onCancel = () => modelSelection.handleModelInputSubmit(null);
      showScreenView(
        `Enter model name for ${getProviderDisplayName(state.pendingProvider)}`,
        'Type or paste the model name from openrouter.ai/models',
        input,
        'Examples: anthropic/claude-3.5-sonnet, openai/gpt-4-turbo, meta-llama/llama-3-70b\nEnter to confirm · esc to go back',
        input,
      );
      return;
    }

    if (state.appState === 'api_key_confirm' && state.pendingProvider) {
      const selector = bindCtrlCExit(createApiKeyConfirmSelector((wantsToSet) =>
        modelSelection.handleApiKeyConfirm(wantsToSet),
      ));
      showScreenView(
        'Set API Key',
        `Would you like to set your ${getProviderDisplayName(state.pendingProvider)} API key?`,
        selector,
        'Enter to confirm · esc to decline',
        selector,
      );
      return;
    }

    if (state.appState === 'api_key_input' && state.pendingProvider) {
      const input = bindCtrlCExit(new ApiKeyInputComponent(true));
      input.onSubmit = (apiKey) => modelSelection.handleApiKeySubmit(apiKey);
      input.onCancel = () => modelSelection.handleApiKeySubmit(null);
      const apiKeyName = getApiKeyNameForProvider(state.pendingProvider) ?? '';
      showScreenView(
        `Enter ${getProviderDisplayName(state.pendingProvider)} API Key`,
        apiKeyName ? `(${apiKeyName})` : '',
        input,
        'Enter to confirm · Esc to cancel',
        input,
      );
      return;
    }

    if (searchState.appState === 'provider_select') {
      const selector = bindCtrlCExit(createSearchProviderSelector(
        searchState.preferredProvider,
        (providerId) => searchSelection.handleProviderSelect(providerId),
        () => searchSelection.cancelSelection(),
      ));
      showScreenView(
        'Select web search provider',
        'Max tries your preferred provider first and falls back to the others.',
        selector,
        'Enter to confirm · esc to exit',
        selector,
      );
      return;
    }

    if (searchState.appState === 'api_key_confirm' && searchState.pendingProvider) {
      const selector = bindCtrlCExit(createApiKeyConfirmSelector((wantsToSet) =>
        searchSelection.handleApiKeyConfirm(wantsToSet),
      ));
      showScreenView(
        'Set API Key',
        `Would you like to set your ${getSearchProviderDisplayName(searchState.pendingProvider)} API key?`,
        selector,
        'Enter to confirm · esc to decline',
        selector,
      );
      return;
    }

    if (searchState.appState === 'api_key_input' && searchState.pendingProvider) {
      const input = bindCtrlCExit(new ApiKeyInputComponent(true));
      input.onSubmit = (apiKey) => searchSelection.handleApiKeySubmit(apiKey);
      input.onCancel = () => searchSelection.handleApiKeySubmit(null);
      const apiKeyName = getApiKeyNameForSearchProvider(searchState.pendingProvider);
      showScreenView(
        `Enter ${getSearchProviderDisplayName(searchState.pendingProvider)} API Key`,
        `(${apiKeyName})`,
        input,
        'Enter to confirm · Esc to cancel',
        input,
      );
      return;
    }

    if (keyState.appState === 'service_select') {
      const items = keyState.services.map((service) => ({
        value: service.id,
        label: keySelection.serviceLabel(service),
      }));
      const selector = bindCtrlCExit(createKeyServiceSelector(items, (serviceId) =>
        keySelection.handleServiceSelect(serviceId),
      ));
      showScreenView(
        'Manage API keys',
        'Keys are stored securely in ~/.max/credentials (owner-only).',
        selector,
        'Enter to manage · esc to exit',
        selector,
      );
      return;
    }

    if (keyState.appState === 'action_select' && keyState.pendingService) {
      const hasKey = checkApiKeyExists(keyState.pendingService.envVar);
      const selector = bindCtrlCExit(createKeyActionSelector(hasKey, (action) =>
        keySelection.handleActionSelect(action),
      ));
      showScreenView(
        keyState.pendingService.displayName,
        `(${keyState.pendingService.envVar})`,
        selector,
        'Enter to confirm · esc to go back',
        selector,
      );
      return;
    }

    if (keyState.appState === 'key_input' && keyState.pendingService) {
      const input = bindCtrlCExit(new ApiKeyInputComponent(true));
      input.onSubmit = (apiKey) => void keySelection.handleKeySubmit(apiKey);
      input.onCancel = () => void keySelection.handleKeySubmit(null);
      showScreenView(
        `Enter ${keyState.pendingService.displayName} API Key`,
        `(${keyState.pendingService.envVar})`,
        input,
        'Enter to confirm · Esc to cancel',
        input,
      );
      return;
    }
  };

  // Wire callbacks that need renderSelectionOverlay (defined above)
  editor.onEscape = () => {
    if (modelSelection.isInSelectionFlow()) {
      modelSelection.cancelSelection();
      return;
    }
    if (searchSelection.isInSelectionFlow()) {
      searchSelection.cancelSelection();
      return;
    }
    if (keySelection.isInSelectionFlow()) {
      keySelection.cancelSelection();
      return;
    }
    if (onboarding.isInSelectionFlow()) {
      onboarding.cancel();
      return;
    }
    if (factoryResetScreen !== 'idle') {
      factoryResetScreen = 'idle';
      pendingFactoryResetScope = null;
      renderSelectionOverlay();
      return;
    }
    if (agentRunner.isProcessing || agentRunner.pendingApproval) {
      agentRunner.cancelExecution();
      return;
    }

    const hasText = editor.getText().trim().length > 0;
    if (hasText) {
      // Double-Esc to clear input
      if (escPendingClear) {
        editor.setText('');
        escPendingClear = false;
        escPendingExit = false;
        if (escTimeout) { clearTimeout(escTimeout); escTimeout = null; }
      } else {
        escPendingClear = true;
        escPendingExit = false;
        if (escTimeout) clearTimeout(escTimeout);
        escTimeout = setTimeout(() => {
          escPendingClear = false;
          updateView();
          tui.requestRender();
        }, 2000);
      }
    } else {
      // Double-Esc to exit
      if (escPendingExit) {
        tui.stop();
        process.exit(0);
      } else {
        escPendingExit = true;
        escPendingClear = false;
        if (escTimeout) clearTimeout(escTimeout);
        escTimeout = setTimeout(() => {
          escPendingExit = false;
          updateView();
          tui.requestRender();
        }, 2000);
      }
    }
    updateView();
    tui.requestRender();
  };

  editor.onSlashChange = (text: string) => {
    slashSuggestions = matchCommands(text);
    slashSelectedIndex = 0;
    slashActive = slashSuggestions.length > 0;
    updateView();
    tui.requestRender();
  };

  editor.onSlashNavigate = (direction: 'up' | 'down') => {
    if (direction === 'down') {
      slashSelectedIndex = Math.min(slashSelectedIndex + 1, slashSuggestions.length - 1);
    } else {
      slashSelectedIndex = Math.max(slashSelectedIndex - 1, 0);
    }
    updateView();
    tui.requestRender();
  };

  editor.onSlashSelect = () => {
    const selected = slashSuggestions[slashSelectedIndex];
    if (selected) {
      slashActive = false;
      slashSuggestions = [];
      editor.setText('');
      void handleSlashCommand(selected.name);
    }
    updateView();
    tui.requestRender();
  };

  editor.onSlashDismiss = () => {
    slashActive = false;
    slashSuggestions = [];
    updateView();
    tui.requestRender();
  };

  await inputHistory.init();
  for (const msg of inputHistory.getMessages().reverse()) {
    editor.addToHistoryWithTruncation(msg);
  }
  renderSelectionOverlay();
  refreshError();

  // First-run onboarding: Max needs both a model key and a Drishti data key for
  // the primary financial research workflow.
  if (onboarding.shouldStart(modelSelection.provider, modelSelection.model)) {
    chatLog.addChild(new Spacer(1));
    chatLog.addChild(
      new Text(
        theme.muted(
          'Max needs a model key and a Drishti key to run financial research.',
        ),
        0,
        0,
      ),
    );
    // Make new users aware Max isn't terminal-only (e.g. WhatsApp).
    chatLog.addChild(new Spacer(1));
    chatLog.addChild(new Text(theme.bold(theme.primary(CHANNEL_GUIDE.title)), 0, 0));
    for (const line of CHANNEL_GUIDE.lines) {
      chatLog.addChild(new Text(theme.muted(line), 0, 0));
    }
    chatLog.addChild(new Text(theme.muted('Type /guide anytime to see everything Max can do.'), 0, 0));
    onboarding.start();
  }

  tui.start();
  void checkForUpdate().then((result) => {
    if (result.status !== 'available') return;
    chatLog.addChild(new Spacer(1));
    chatLog.addChild(
      new Text(theme.warning(`Update available: ${UPDATE_CHECK_PACKAGE_NAME} v${result.latestVersion}`), 0, 0),
    );
    chatLog.addChild(new Text(`${theme.muted('Run: ')}${theme.bold(theme.primaryLight(result.command))}`, 0, 0));
    tui.requestRender();
  });
  await new Promise<void>((resolve) => {
    const finish = () => resolve();
    process.once('exit', finish);
    process.once('SIGINT', finish);
    process.once('SIGTERM', finish);
  });

  workingIndicator.dispose();
  debugPanel.dispose();
}
